/**
 * Mixpanel Analytics Integration
 *
 * ⚠️ IMPORTANT: HIPAA/GDPR Compliance Warning
 * - NEVER track patient data (names, IDs, medical records)
 * - Employee names and emails are only collected in approved staging and production environments
 * - Only track anonymized, aggregate business metrics
 * - Ensure legal/compliance team approval before deployment
 */

import type { Dict } from "mixpanel-browser";
import mixpanel from "mixpanel-browser";

interface MixpanelConfig {
  token: string;
  enabled: boolean;
  debug: boolean;
  environment: string;
}

class MixpanelAnalytics {
  private config: MixpanelConfig;
  private initialized = false;

  constructor() {
    this.config = {
      token: import.meta.env.VITE_MIXPANEL_TOKEN || "",
      enabled:
        !!import.meta.env.VITE_MIXPANEL_TOKEN && import.meta.env.VITE_MIXPANEL_ENABLED !== "false",
      debug: import.meta.env.MODE !== "production",
      environment: import.meta.env.VITE_ENV || "development",
    };
  }

  private allowSensitiveUserProps(): boolean {
    return this.config.environment === "staging" || this.config.environment === "production";
  }

  private isSensitiveKey(key: string): boolean {
    const patterns = [
      /^\$email$/i,
      /^\$name$/i,
      /^\$first_name$/i,
      /^\$last_name$/i,
      /^email$/i,
      /email/i,
      /^phone$/i,
      /phone/i,
      /mobile/i,
      /tel/i,
      /^username$/i,
      /user_name/i,
      /^name$/i,
      /full_name/i,
      /first_name/i,
      /last_name/i,
      /address/i,
    ];
    return patterns.some((pattern) => pattern.test(key));
  }

  private isSensitiveValue(value: unknown): boolean {
    if (typeof value !== "string") return false;
    const emailRegex = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
    const phoneRegex = /\+?\d[\d\s-]{6,}\d/;
    return emailRegex.test(value) || phoneRegex.test(value);
  }

  private sanitizeUserProperties(properties: Dict): Dict {
    if (this.allowSensitiveUserProps()) return properties;
    const sanitized: Dict = {};
    for (const [key, value] of Object.entries(properties)) {
      if (this.isSensitiveKey(key)) continue;
      if (this.isSensitiveValue(value)) continue;
      sanitized[key] = value;
    }
    return sanitized;
  }

  private isSensitiveUserId(userId: string): boolean {
    const emailRegex = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
    return emailRegex.test(userId);
  }

  private withEnvironment(properties: Dict): Dict {
    return {
      ...properties,
      environment: this.config.environment,
    };
  }
  /**
   * Initialize Mixpanel
   * Call this in main.tsx or App.tsx
   */
  initialize() {
    if (!this.config.token) {
      console.warn("[Mixpanel] Token not configured");
      return;
    }

    if (this.initialized) {
      console.warn("[Mixpanel] Already initialized");
      return;
    }

    try {
      mixpanel.init(this.config.token, {
        debug: this.config.debug,
        track_pageview: false, // We'll track manually for better control
        persistence: "localStorage",
        // ECHO-2318 / VAPT-006: send immediately; batched payloads would sit in localStorage (__mpq_*)
        batch_requests: false,
        ignore_dnt: false, // Respect Do Not Track
        ip: true, // Enable IP tracking to get city/country (⚠️ Note: Consider privacy compliance)
        property_blacklist: ["$current_url", "$initial_referrer", "$referrer"], // Remove potentially sensitive data
        loaded: () => {
          if (this.config.debug) {
            console.log("[Mixpanel] Initialized successfully");
          }
        },
      });

      // Set super properties (sent with every event)
      mixpanel.register({
        environment: this.config.environment,
        app_version: import.meta.env.VITE_APP_VERSION || "unknown",
        platform: "web",
      });

      this.initialized = true;
    } catch (error) {
      console.error("[Mixpanel] Initialization failed:", error);
    }
  }

  /**
   * Check if Mixpanel is ready
   */
  private isReady(): boolean {
    if (!this.config.enabled) {
      if (this.config.debug) {
        console.log("[Mixpanel] Disabled");
      }
      return false;
    }

    if (!this.initialized) {
      console.warn("[Mixpanel] Not initialized. Call initialize() first.");
      return false;
    }

    return true;
  }

  /**
   * Track custom event
   * @param eventName - Name of the event
   * @param properties - Event properties (NO PII!)
   *
   * @example
   * mixpanel.track('Consultation Saved', {
   *   save_type: 'auto',
   *   duration_seconds: 120,
   *   has_attachments: true
   * });
   */
  track(eventName: string, properties?: Dict) {
    if (!this.isReady()) return;

    try {
      mixpanel.track(eventName, {
        ...properties,
        timestamp: new Date().toISOString(),
      });

      if (this.config.debug) {
        console.log("[Mixpanel] Track:", eventName, properties);
      }
    } catch (error) {
      console.error("[Mixpanel] Track failed:", error);
    }
  }

  /**
   * Track page view
   * @param pageName - Name of the page (e.g., 'Consultation', 'Patient List')
   * @param properties - Additional properties (NO PII!)
   */
  trackPageView(pageName: string, properties?: Dict) {
    this.track("Page View", {
      page_name: pageName,
      ...properties,
    });
  }

  /**
   * Identify user (use anonymized/hashed ID only!)
   * @param userId - Anonymized user ID (NOT email, NOT name)
   *
   * @example
   * // Good: Use hashed or system-generated ID
   * mixpanel.identify('user_abc123');
   *
   * // Bad: NEVER use email or name
   * // mixpanel.identify('john.doe@example.com'); ❌
   */
  identify(userId: string) {
    if (!this.isReady()) return;

    if (!this.allowSensitiveUserProps() && this.isSensitiveUserId(userId)) {
      console.warn("[Mixpanel] identify blocked: userId looks like email");
      return;
    }
    try {
      mixpanel.identify(userId);

      if (this.config.debug) {
        console.log("[Mixpanel] Identify:", userId);
      }
    } catch (error) {
      console.error("[Mixpanel] Identify failed:", error);
    }
  }

  /**
   * Set user properties.
   * Staging and production may include approved employee PII; patient data is never allowed.
   *
   * @example
   * mixpanel.setUserProperties({
   *   role: 'doctor',
   *   clinic_type: 'outpatient',
   *   account_created: '2024-01-01'
   * });
   */
  setUserProperties(properties: Dict) {
    if (!this.isReady()) return;

    try {
      const sanitized = this.sanitizeUserProperties(this.withEnvironment(properties));
      if (Object.keys(sanitized).length === 0) {
        if (this.config.debug) {
          console.warn("[Mixpanel] Set user properties skipped (sanitized to empty)");
        }
        return;
      }

      mixpanel.people.set(sanitized);

      if (this.config.debug) {
        console.log("[Mixpanel] Set user properties:", sanitized);
      }
    } catch (error) {
      console.error("[Mixpanel] Set user properties failed:", error);
    }
  }

  /**
   * Increment user property
   * @param property - Property name
   * @param value - Increment value (default: 1)
   *
   * @example
   * mixpanel.incrementUserProperty('consultations_completed', 1);
   */
  incrementUserProperty(property: string, value = 1) {
    if (!this.isReady()) return;

    try {
      mixpanel.people.increment(property, value);

      if (this.config.debug) {
        console.log("[Mixpanel] Increment:", property, value);
      }
    } catch (error) {
      console.error("[Mixpanel] Increment failed:", error);
    }
  }

  /**
   * Track time for an event (start timing)
   * @param eventName - Event name to time
   *
   * @example
   * mixpanel.timeEvent('Consultation Save');
   * // ... do work ...
   * mixpanel.track('Consultation Save'); // Will include duration
   */
  timeEvent(eventName: string) {
    if (!this.isReady()) return;

    try {
      mixpanel.time_event(eventName);

      if (this.config.debug) {
        console.log("[Mixpanel] Time event started:", eventName);
      }
    } catch (error) {
      console.error("[Mixpanel] Time event failed:", error);
    }
  }

  /**
   * Register super properties (sent with every event)
   * @param properties - Properties to register
   *
   * @example
   * mixpanel.registerSuperProperties({
   *   clinic_id: 'clinic_123',
   *   user_role: 'doctor'
   * });
   */
  registerSuperProperties(properties: Dict) {
    if (!this.isReady()) return;

    try {
      const sanitized = this.sanitizeUserProperties(this.withEnvironment(properties));
      if (Object.keys(sanitized).length === 0) {
        if (this.config.debug) {
          console.warn("[Mixpanel] Register super properties skipped (sanitized to empty)");
        }
        return;
      }

      mixpanel.register(sanitized);

      if (this.config.debug) {
        console.log("[Mixpanel] Register super properties:", sanitized);
      }
    } catch (error) {
      console.error("[Mixpanel] Register super properties failed:", error);
    }
  }

  /**
   * Unregister super property
   * @param property - Property name to unregister
   */
  unregisterSuperProperty(property: string) {
    if (!this.isReady()) return;

    try {
      mixpanel.unregister(property);

      if (this.config.debug) {
        console.log("[Mixpanel] Unregister super property:", property);
      }
    } catch (error) {
      console.error("[Mixpanel] Unregister super property failed:", error);
    }
  }

  /**
   * Reset user (call on logout)
   * Clears all user data and generates new distinct_id
   */
  reset() {
    if (!this.isReady()) return;

    try {
      mixpanel.reset();

      if (this.config.debug) {
        console.log("[Mixpanel] Reset");
      }
    } catch (error) {
      console.error("[Mixpanel] Reset failed:", error);
    }
  }

  /**
   * Opt user out of tracking
   */
  optOut() {
    if (!this.isReady()) return;

    try {
      mixpanel.opt_out_tracking();

      if (this.config.debug) {
        console.log("[Mixpanel] Opt out");
      }
    } catch (error) {
      console.error("[Mixpanel] Opt out failed:", error);
    }
  }

  /**
   * Opt user in to tracking
   */
  optIn() {
    if (!this.isReady()) return;

    try {
      mixpanel.opt_in_tracking();

      if (this.config.debug) {
        console.log("[Mixpanel] Opt in");
      }
    } catch (error) {
      console.error("[Mixpanel] Opt in failed:", error);
    }
  }

  /**
   * Check if user has opted out
   */
  hasOptedOut(): boolean {
    if (!this.isReady()) return false;

    try {
      return mixpanel.has_opted_out_tracking();
    } catch (error) {
      console.error("[Mixpanel] Check opt out failed:", error);
      return false;
    }
  }

  /**
   * Get distinct ID
   */
  getDistinctId(): string | undefined {
    if (!this.isReady()) return undefined;

    try {
      return mixpanel.get_distinct_id();
    } catch (error) {
      console.error("[Mixpanel] Get distinct ID failed:", error);
      return undefined;
    }
  }

  /**
   * Alias user (link anonymous ID to identified user)
   * @param alias - New user ID
   * @param distinctId - Original distinct ID (optional)
   */
  alias(alias: string, distinctId?: string) {
    if (!this.isReady()) return;

    try {
      mixpanel.alias(alias, distinctId);

      if (this.config.debug) {
        console.log("[Mixpanel] Alias:", alias, distinctId);
      }
    } catch (error) {
      console.error("[Mixpanel] Alias failed:", error);
    }
  }
}

// Export singleton instance
export const mixpanelAnalytics = new MixpanelAnalytics();

// Export class for testing
export { MixpanelAnalytics };
